import { FC, useState, MouseEvent } from 'react';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MainLayout from 'src/pages/private';
import { useFormik } from 'formik';
import {
  orderFilterInitialValues,
  purchaseOrderInitialValues,
} from 'src/utils/initialValues.utils';
import {
  purchaseOrderValidation,
  orderFilterValidation,
} from 'src/utils/validationScheme.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  apiUrls,
  dataStorageKeywords,
  errorMessages,
} from 'src/utils/properties.utils';
import Cookies from 'js-cookie';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import SearchBar from 'src/components/InputFields/Searchbar';
import PurchaseOrderGrid from 'src/components/Grids/PurchaseOrderGrid';
import TextField from 'src/components/InputFields/TextField';
import PurchaseOrderDialog from 'src/components/DialogBox/POBox/OrderBox';
import VendorSelect from 'src/components/InputFields/VendorPicker';
import TextArea from 'src/components/InputFields/TextArea';
import RawMaterialSelect from 'src/components/InputFields/RawMaterialPicker';
import FilterPopover from 'src/components/StyledComponents/FilterPopover';
import FormikSelectField, {
  Option,
} from 'src/components/InputFields/FormikSelectField';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { calculatePurchaseOrderDialogHeight } from 'src/utils/purchaseOrderActionMenu.utils';
import { PurchaseOrderStatus } from 'src/models/enums/PurchaseOrderStatus.enum';
import { getBasicMaterials } from 'src/services/material.service';
import {
  PrimaryActionButton,
  ButtonIcon,
  FilterButton,
  FilterIcon,
  FilterPopoverIcon,
  FilterPopoverTitle,
  FilterPopoverDivider,
  FilterCloseIcon,
  FilterLabel,
  SmallActionButton,
  AddItemIcon,
  AddItemText,
  SpacedContainer,
  FlexStartContainer,
  ClickableContainer,
  RemoveItemIcon,
  OverflowContainer,
  ErrorHelperTextContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import {
  FlexRowStartBox,
  FlexColumnBox,
  BorderedSectionBox,
  SectionTitleTypography,
  ItemRowBox,
  ItemFieldBox,
  ItemFieldAlignedBox,
  RemoveItemBox,
} from 'src/components/DialogBox/POBox/POBox.styles';

const PurchaseOrderManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const statusOptions: Option[] = [
    { label: 'New', value: PurchaseOrderStatus.NEW },
    { label: 'Pending', value: PurchaseOrderStatus.PENDING },
    { label: 'Approved', value: PurchaseOrderStatus.APPROVED },
    { label: 'Rejected', value: PurchaseOrderStatus.REJECTED },
  ];

  const [open, setOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingSaveComplete, setLoadingSaveComplete] = useState(false);
  const [orderAdded, setOrderAdded] = useState(false);
  const { showSnackbar } = StyledSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [orderStatus, setOrderStatus] = useState<string>(
    PurchaseOrderStatus.NEW
  );
  const [items, setItems] = useState([
    { rawMaterialId: '', requestedQty: '', id: Date.now().toString() },
  ]);
  const [filterValues, setFilterValues] = useState({
    vendorId: '',
    status: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });
  const [rawMaterials, setRawMaterials] = useState<
    { id: string; name: string }[]
  >([]);

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = async () => {
    setOpen(true);
    if (rawMaterials.length === 0) {
      try {
        const materials = await getBasicMaterials();
        setRawMaterials(materials);
      } catch (error) {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    }
  };

  const handleClose = () => {
    const defaultItems = [
      { rawMaterialId: '', requestedQty: '', id: Date.now().toString() },
    ];
    setItems(defaultItems);

    // Reset form with fresh initial values
    const dynamicFields: Record<string, string> = {};
    defaultItems.forEach((item) => {
      dynamicFields[`rawMaterialId-${item.id}`] = '';
      dynamicFields[`requestedQty-${item.id}`] = '';
    });
    formik.resetForm({
      values: {
        ...purchaseOrderInitialValues,
        ...dynamicFields,
      },
    });

    setOrderStatus(PurchaseOrderStatus.NEW);
    setOpen(false);
  };

  const handleFilterClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setFilterOpen(true);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setFilterOpen(false);
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    if (orderStatus === PurchaseOrderStatus.NEW) {
      setLoadingSave(true);
    } else if (orderStatus === PurchaseOrderStatus.PENDING) {
      setLoadingSaveComplete(true);
    }
    const preparedBy = Cookies.get(dataStorageKeywords.USER_ID);

    const purchaseItems = items
      .map((item) => {
        const rawMaterialId = values[`rawMaterialId-${item.id}`];
        const requestedQty = values[`requestedQty-${item.id}`];

        return { rawMaterialId, requestedQty };
      })
      .filter((item) => item.rawMaterialId && Number(item.requestedQty) > 0);

    if (purchaseItems.length === 0) {
      showSnackbar(errorMessages.PLEASE_ADD_AT_LEAST_ONE_VALID_ITEM, 'error');
      setLoadingSave(false);
      setLoadingSaveComplete(false);
      return;
    }

    try {
      const response = await axiosTokenInstance.post(
        apiUrls.ADD_PURCHASE_ORDERS,
        {
          vendorId: values.vendor,
          deliveryAddress: values.deliveryAddress,
          preparedBy,
          purchaseItems,
          status: orderStatus,
        }
      );
      showSnackbar(response.data.message, 'success');
      setOrderAdded(!orderAdded);
      setOrderStatus(PurchaseOrderStatus.NEW);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoadingSave(false);
      setLoadingSaveComplete(false);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      rawMaterialId: '',
      requestedQty: '',
      id: Date.now().toString(),
    };
    setItems([...items, newItem]);

    // Preserve existing form values and add new fields for the new item
    formik.setValues({
      ...formik.values,
      [`rawMaterialId-${newItem.id}`]: '',
      [`requestedQty-${newItem.id}`]: '',
    });
  };

  const handleStatusChange = async (status: PurchaseOrderStatus) => {
    setOrderStatus(status);

    setTimeout(async () => {
      const errors = await formik.validateForm();

      if (Object.keys(errors).length === 0) {
        formik.handleSubmit();
      } else {
        formik.setTouched(
          Object.keys(formik.values).reduce(
            (acc, key) => ({ ...acc, [key]: true }),
            {}
          )
        );
      }
    }, 0);
  };

  const handleRemoveItem = (id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));

    // Remove the formik values for the removed item
    const newValues = { ...formik.values };
    delete (newValues as Record<string, unknown>)[`rawMaterialId-${id}`];
    delete (newValues as Record<string, unknown>)[`requestedQty-${id}`];
    formik.setValues(newValues);
  };

  const getInitialValues = () => {
    const dynamicFields: Record<string, string> = {};
    items.forEach((item) => {
      dynamicFields[`rawMaterialId-${item.id}`] = '';
      dynamicFields[`requestedQty-${item.id}`] = '';
    });
    return {
      ...purchaseOrderInitialValues,
      ...dynamicFields,
    };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: purchaseOrderValidation(items),
    enableReinitialize: false,
    validateOnChange: true,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const filterFormik = useFormik({
    initialValues: orderFilterInitialValues,
    validationSchema: orderFilterValidation,
    onSubmit: (values) => {
      setFilterValues({
        vendorId: values.vendorPicker,
        status: values.statusPicker,
        startDate: values.startDate,
        endDate: values.endDate,
      });
      handleFilterClose();
    },
  });

  const selectedMaterialIds = items
    .map(
      (item) =>
        (formik.values as unknown as Record<string, string>)[
          `rawMaterialId-${item.id}`
        ]
    )
    .filter(Boolean);

  return (
    <MainLayout
      title="Purchase Order Management"
      searchBar={
        <SearchBar
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search purchase orders by vendor"
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="220px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New Purchase Order
        </PrimaryActionButton>
      }
      filterButton={
        <FilterButton
          className="btn-filter"
          width="100px"
          onClick={handleFilterClick}
        >
          <FilterIcon component="i" className="bx bx-filter" />
          Add Filter
        </FilterButton>
      }
    >
      <FilterPopover
        open={filterOpen}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
      >
        <Box>
          <Box display="flex" alignItems="center">
            <FilterPopoverIcon component="i" className="bx bx-filter" />
            <FilterPopoverTitle>Filter Data By</FilterPopoverTitle>
          </Box>
          <FilterPopoverDivider />
          <FilterCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleFilterClose}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" mb={1}>
          <VendorSelect
            id="vendorPicker"
            label="Vendor"
            height="28px"
            width="150px"
            customMessage="Vendor"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
            useBasicVendors={true}
          />

          <FormikSelectField
            id="statusPicker"
            label="Status"
            showLabel={false}
            formikProps={filterFormik}
            customMessage="Status"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            height="28px"
            width="150px"
            options={statusOptions}
          />
        </Box>

        <Box>
          <FilterLabel>Created at:</FilterLabel>

          <SpacedContainer display="flex" justifyContent="space-between">
            <CreatedDatePicker
              name="startDate"
              placeholder="Start Date"
              formikProps={filterFormik}
              fontSize="10px"
            />

            <CreatedDatePicker
              name="endDate"
              placeholder="End Date"
              formikProps={filterFormik}
              fontSize="10px"
            />
          </SpacedContainer>
          {filterFormik.touched.endDate && filterFormik.errors.endDate && (
            <ErrorHelperTextContainer>
              {filterFormik.errors.endDate}
            </ErrorHelperTextContainer>
          )}
        </Box>

        <Box mt={4} display="flex" justifyContent="space-between">
          <SmallActionButton
            btnVariant="inactive"
            width="70px"
            mr={2}
            onClick={() => {
              filterFormik.resetForm();
              setFilterValues({
                vendorId: '',
                status: '',
                startDate: null,
                endDate: null,
              });
            }}
          >
            Clear
          </SmallActionButton>

          <SmallActionButton
            className="btn-primary"
            width="70px"
            onClick={() => filterFormik.handleSubmit()}
          >
            Apply
          </SmallActionButton>
        </Box>
      </FilterPopover>

      <PurchaseOrderGrid
        state={orderAdded}
        searchTerm={searchTerm}
        filters={filterValues}
      />
      <Box display="flex">
        <PurchaseOrderDialog
          height={`${calculatePurchaseOrderDialogHeight(formik)}px`}
          open={open}
          handleClose={handleClose}
          title="Add New Purchase Order"
          subtitle="Fill in the details below to create a new purchase order."
          formTitle="Order Details"
          onSubmit={formik.handleSubmit}
          smScreen={smScreen}
          button1Loading={loadingSave}
          button2Loading={loadingSaveComplete}
          buttonLabel2="Save and Complete"
          onButtonClick={handleStatusChange}
        >
          <Box display="flex" flexDirection="column">
            <FlexRowStartBox>
              <FlexColumnBox>
                <VendorSelect
                  color="var(--subtext)"
                  width="300px"
                  id="vendor"
                  label="Vendor"
                  formikProps={formik}
                />
              </FlexColumnBox>

              <FlexStartContainer>
                <TextArea
                  color="var(--subtext)"
                  id="deliveryAddress"
                  label="Delivery Address"
                  formikProps={formik}
                  rows={5}
                  height="140px"
                  overflowY="hidden"
                />
              </FlexStartContainer>
            </FlexRowStartBox>

            <BorderedSectionBox>
              <SectionTitleTypography>Item Details</SectionTitleTypography>

              {items.map((item, index) => (
                <ItemRowBox key={item.id}>
                  <ItemFieldBox>
                    <RawMaterialSelect
                      showLabel={index === 0}
                      color="var(--subtext)"
                      width="300px"
                      id={`rawMaterialId-${item.id}`}
                      label="Item"
                      formikProps={formik}
                      selectedIds={selectedMaterialIds}
                      rawMaterialsList={rawMaterials}
                    />
                  </ItemFieldBox>
                  <ItemFieldAlignedBox>
                    <TextField
                      showLabel={index === 0}
                      color="var(--subtext)"
                      type="number"
                      width="300px"
                      id={`requestedQty-${item.id}`}
                      label="Requested Quantity"
                      formikProps={formik}
                    />
                  </ItemFieldAlignedBox>
                  {items.length > 1 && (
                    <RemoveItemBox>
                      <RemoveItemIcon
                        component="i"
                        className="bx bx-minus-circle 1"
                        onClick={() => handleRemoveItem(item.id)}
                      />
                    </RemoveItemBox>
                  )}
                </ItemRowBox>
              ))}
            </BorderedSectionBox>
            <ClickableContainer
              width="100px"
              display="flex"
              mt={2}
              mb={2}
              onClick={handleAddItem}
            >
              <AddItemIcon component="i" className="bx bx-plus" />
              <AddItemText>Add Item</AddItemText>
            </ClickableContainer>
          </Box>
        </PurchaseOrderDialog>
      </Box>
    </MainLayout>
  );
};

export default PurchaseOrderManagement;
